import { useState, useEffect, useCallback } from 'react';

import { componentLogger } from '@ragu-components/logger';
import { useOrdIdentity } from '@ragu-components/ord-providers';
import { Resources } from '@ragu-components/ord-resources';
import { ServiceClassFields } from '@ragu-components/ord-types';
import { canAccessWithPermissions } from '@ratto-components/ra-rbac';
import { usePermissions } from 'react-admin';

import { useIngordDataProvider } from 'src/components/flowchart/hooks/useIngordDataProvider';

import type { ServiceClass, ServiceClassPasswordPolicy } from '@ragu-components/ord-types';
import type { AxiosError } from 'axios';

interface UseGetServiceClassResult {
  passwordPolicy?: ServiceClassPasswordPolicy;
  isLoading: boolean;
}

type DefaultCosType = 'user_default' | 'sip_default';
type PersonalCosType = 'personal';

type UseGetPasswordPolicyParams =
  | {
      serviceClassId: string;
    }
  | { cosType: DefaultCosType | PersonalCosType };

export const useGetPasswordPolicy = (params: UseGetPasswordPolicyParams): UseGetServiceClassResult => {
  const { identity } = useOrdIdentity();
  const [passwordPolicy, setPasswordPolicy] = useState<ServiceClassPasswordPolicy>();
  const [isLoading, setIsLoading] = useState(true);
  const [hasPermissionError, setHasPermissionError] = useState(false);
  const dataProvider = useIngordDataProvider();

  const { permissions } = usePermissions();
  const canShowServiceClasses = canAccessWithPermissions({
    permissions,
    action: 'list',
    resource: Resources.service_classes,
  });

  const getDefaultCos = useCallback(
    async (cosType: DefaultCosType) => {
      if (!canShowServiceClasses) {
        setHasPermissionError(true);
        return undefined;
      }
      try {
        const response = await dataProvider.getList(Resources.service_classes, {
          filter: {
            active: 'fiql',
            fiql: `(${ServiceClassFields.is_default}=='true')`,
          },
          pagination: { page: 1, perPage: 25 },
          sort: { field: ServiceClassFields.id, order: 'ASC' },
        });
        return (response.data?.[0] as ServiceClass)?.[
          cosType === 'sip_default' ? 'sip_password_policy' : 'password_policy'
        ];
      } catch (error) {
        if ((error as AxiosError).response?.status === 403) {
          setHasPermissionError(true);
        }
        throw error;
      }
    },
    [dataProvider, canShowServiceClasses]
  );

  const getUserCos = useCallback(
    async (serviceClassId: string) => {
      if (!canShowServiceClasses) {
        setHasPermissionError(true);
        return undefined;
      }
      try {
        const response = await dataProvider.getOne<ServiceClass>(Resources.service_classes, {
          id: serviceClassId ?? '',
        });
        return response.data?.password_policy;
      } catch (error) {
        if ((error as AxiosError).response?.status === 403) {
          setHasPermissionError(true);
        }
        throw error;
      }
    },
    [dataProvider, canShowServiceClasses]
  );

  useEffect(() => {
    // Don't retry if we already have a permission error
    if (hasPermissionError) {
      setIsLoading(false);
      return;
    }

    const getPolicy = async (): Promise<void> => {
      try {
        if ('serviceClassId' in params) {
          const userCos = await getUserCos(params.serviceClassId);
          setPasswordPolicy(userCos);
        } else if (params.cosType === 'personal') {
          const personalPasswordPolicy = identity?.password_policy;
          setPasswordPolicy(personalPasswordPolicy);
        } else {
          const defaultCos = await getDefaultCos(params.cosType);
          setPasswordPolicy(defaultCos);
        }
      } catch (error) {
        componentLogger.warn('Unable to fetch session: ', error);
      } finally {
        setIsLoading(false);
      }
    };

    if (passwordPolicy === undefined) {
      setIsLoading(true);
      getPolicy().catch(() => {});
    }
  }, [getDefaultCos, getUserCos, params, passwordPolicy, identity, hasPermissionError]);

  return { passwordPolicy, isLoading };
};
